import { TOKEN_NAMESPACE } from '@/api/apiClienttFactory'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { useAuth } from 'react-oidc-context'
import useWebSocketBase from 'react-use-websocket'
import type { Options } from 'react-use-websocket'
import { useRef } from 'react'

export type WebSocketHook = {
  sendJsonMessage: (message: Record<string, unknown>) => void
  lastJsonMessage: Record<string, unknown> | null
  lastMessage: MessageEvent | null
  sendMessage: (
    message: string | ArrayBufferLike | Blob | ArrayBufferView,
  ) => void
  getWebSocket: () => unknown
  readyState: number
}

function castLastJsonMessage(value: unknown): Record<string, unknown> | null {
  if (value === null || value === undefined) return null
  return value as Record<string, unknown>
}
export default function useWebSocket(
  url: string | (() => string | Promise<string>) | null,
  { onClose, onError, onOpen, ...options }: Options = {},
  connect = true,
): WebSocketHook {
  const { signoutRedirect } = useAuth()
  const { t } = useTranslation(['app', 'common'])
  const { enqueueSnackbar } = useSnackbar()
  const shouldNotReconnectRef = useRef(() => false)

  const {
    sendJsonMessage,
    lastJsonMessage,
    lastMessage,
    sendMessage,
    getWebSocket,
    readyState,
  } = useWebSocketBase(
    url,
    {
      onClose: (event) => {
        const { code, reason } = event
        if (code >= 4400) {
          if (code === 4401) {
            enqueueSnackbar(t('common:request.invalidCredentials'), {
              variant: 'error',
            })
            signoutRedirect().catch((error: unknown) => {
              console.error('Sign out failed:', error)
            })
          } else {
            enqueueSnackbar(
              `${t('app:device.calibration.wsError')}: ${reason}`,
              { variant: 'error' },
            )
          }
        }
        onClose?.(event)
      },
      onError: (event) => {
        enqueueSnackbar(
          `${t('app:device.calibration.wsError')}: ${event.type}`,
          { variant: 'error' },
        )
        onError?.(event)
      },
      onOpen: (event) => {
        const token = localStorage.getItem(TOKEN_NAMESPACE)
        sendJsonMessage({
          type: 'authenticated',
          data: token ? `Bearer ${token}` : '',
        })
        onOpen?.(event)
      },
      shouldReconnect: shouldNotReconnectRef.current,
      ...options,
    },
    connect,
  )

  return {
    sendJsonMessage,
    lastJsonMessage: castLastJsonMessage(lastJsonMessage),
    sendMessage,
    lastMessage,
    getWebSocket,
    readyState,
  }
}
